import { PlayRequestStatusesEnum } from 'types/generated/client';
import CheckCircle from 'svg/CheckCircle';
import CloseIcon from 'svg/CloseIcon';
import PaperPlane from 'svg/PaperPlane';
import classNames from 'styles/utils/classNames';

interface Props {
  status: PlayRequestStatusesEnum;
  /** `sm` is the chat-list row (10px label); `md` the request card in a thread. */
  size?: 'sm' | 'md';
  className?: string;
}

const STATUS_LABELS: Record<PlayRequestStatusesEnum, string> = {
  [PlayRequestStatusesEnum.Pending]: 'Pending',
  [PlayRequestStatusesEnum.Accepted]: 'Accepted',
  [PlayRequestStatusesEnum.Declined]: 'Declined',
  [PlayRequestStatusesEnum.Cancelled]: 'Cancelled',
  [PlayRequestStatusesEnum.AutoCancelled]: 'Spots taken',
  [PlayRequestStatusesEnum.Expired]: 'Expired',
};

// Pending and Accepted come straight from Figma ("Badge Activity type"): brand-yellow
// and brand-green 100/800, which match the design variables exactly. The three
// closed states have no design yet and share a neutral treatment.
const STATUS_COLORS: Record<PlayRequestStatusesEnum, string> = {
  [PlayRequestStatusesEnum.Pending]: 'bg-brand-yellow-100 text-brand-yellow-800',
  [PlayRequestStatusesEnum.Accepted]: 'bg-brand-green-100 text-brand-green-800',
  [PlayRequestStatusesEnum.Declined]:
    'bg-color-bg-lightmode-secondary text-color-text-lightmode-secondary',
  [PlayRequestStatusesEnum.Cancelled]:
    'bg-color-bg-lightmode-secondary text-color-text-lightmode-secondary',
  [PlayRequestStatusesEnum.AutoCancelled]:
    'bg-color-bg-lightmode-secondary text-color-text-lightmode-secondary',
  [PlayRequestStatusesEnum.Expired]:
    'bg-color-bg-lightmode-secondary text-color-text-lightmode-secondary',
};

/** Status pill for a play request — shown beside a name in the chat list and at the
 *  top of the request card in a thread. */
export default function RequestStatusBadge({ status, size = 'md', className }: Props) {
  const isSmall = size === 'sm';
  const iconClass = isSmall ? 'size-3 shrink-0' : 'size-3.5 shrink-0';

  const icon =
    status === PlayRequestStatusesEnum.Pending ? (
      <PaperPlane className={iconClass} />
    ) : status === PlayRequestStatusesEnum.Accepted ? (
      <CheckCircle className={iconClass} />
    ) : (
      <CloseIcon className={iconClass} />
    );

  return (
    <span
      className={classNames(
        'inline-flex shrink-0 items-center justify-center gap-[0.125rem] rounded-2xl px-ds-sm py-ds-xs',
        isSmall ? 'typography-product-button-label-xs' : 'typography-product-button-label-small',
        STATUS_COLORS[status],
        className,
      )}
    >
      {icon}
      {STATUS_LABELS[status]}
    </span>
  );
}
